import { EmailContentDefinition } from 'modules/graphql/types';
import { ReactNode, useState } from 'react';
import styled from 'styled-components';
import { Add16, Close16 } from 'ui/icons';
import { Button, BUTTON_VARIANTS } from 'ui/button';
import { spacing } from 'ui/theme';
import { pxToRem } from 'utils/convert';
import {
  FieldNames,
  MessageTypeSelect,
  SettingsSectionHeader,
} from 'modules/templateEditor/common';
import { MESSAGE_MEDIUM_DATA_ARRAY_DELIMITER } from 'modules/templateEditor/common/constants';
import { constructFieldErrors, getIsEditMode } from 'modules/templateEditor/common/utils';
import { useEditableFieldsContext } from 'modules/templateEditor/context/EditableFieldsContext';
import { useQueryParamContext } from 'modules/templateEditor/context/QueryParamContext';
import { useTranslations } from 'modules/templateEditor/hooks/useTranslations';
import { useErrorValidationContext } from 'modules/templateEditor/context/ErrorValidationContext';
import { constructErrorInlineText } from 'modules/templateEditor/common/ErrorInlineText';
import { FromEmailField } from './FromEmailField';
import { Text } from './Text';

type TextFieldAttributes = Pick<
  EmailContentDefinition,
  'fromName' | 'fromEmail' | 'replyTo' | 'ccEmails' | 'bccEmails'
>;
type TextFieldMediumDataKey = keyof TextFieldAttributes;

type TextFieldProps = {
  mediumDataKey: TextFieldMediumDataKey;
  helpText: string;
  labelText: string;
  placeholder?: string;
  valueIsArray?: boolean;
  hasError?: boolean;
  errorText?: string | JSX.Element;
  Suffix?: ReactNode | null;
};

type AddButtonProps = {
  text: string;
  testid: string;
  display: boolean;
  onClick: () => void;
};

const ButtonWrapper = styled.div`
  display: flex;
  margin: ${spacing.base} 0 ${spacing.plusHalf} ${pxToRem(185)};
`;

const CloseButton = styled.div`
  display: flex;
  cursor: pointer;
`;

const AddButton = ({ text, testid, display, onClick }: AddButtonProps) => (
  <>
    {display && (
      <Button data-testid={testid} variant={BUTTON_VARIANTS.GHOST} onClick={onClick}>
        <Add16 />
        &nbsp;{text}
      </Button>
    )}
  </>
);

/**
 * We use the array delimiter to split our text as an array.
 * We also trim the empty spaces. Since, emails can't have spaces.
 *
 * @param value string
 * @returns string[]
 */
const processTextArray = (value: string) =>
  value.length === 0
    ? []
    : value.split(MESSAGE_MEDIUM_DATA_ARRAY_DELIMITER).map((val) => val.trim());

export const SendingInformationSection = () => {
  const { t } = useTranslations();
  const { view } = useQueryParamContext();
  const { isEditMode, liveTemplate, setCurrentMediumField } = useEditableFieldsContext();
  const { localeContentErrors, localeContentWarnings } = useErrorValidationContext();
  const isEditModeVisible = getIsEditMode(view, isEditMode);
  const emailData = (liveTemplate?.mediumData || {}) as EmailContentDefinition;
  const [showCCField, setCCFieldState] = useState((emailData?.ccEmails || []).length > 0);
  const [showBCCField, setBCCFieldState] = useState((emailData?.bccEmails || []).length > 0);
  const showCarbonCopyButtons = isEditModeVisible && (!showCCField || !showBCCField);

  const { hasError: hasReplyToError } = constructFieldErrors({
    contentErrors: localeContentErrors,
    contentWarnings: localeContentWarnings,
    fieldName: FieldNames.REPLY_TO,
  });

  const { hasError: hasFromNameError } = constructFieldErrors({
    contentErrors: localeContentErrors,
    contentWarnings: localeContentWarnings,
    fieldName: FieldNames.FROM_NAME,
  });

  const { hasError: hasCCError } = constructFieldErrors({
    contentErrors: localeContentErrors,
    contentWarnings: localeContentWarnings,
    fieldName: FieldNames.CC_EMAILS,
  });

  const { hasError: hasBCCError } = constructFieldErrors({
    contentErrors: localeContentErrors,
    contentWarnings: localeContentWarnings,
    fieldName: FieldNames.BCC_EMAILS,
  });

  const replyToErrorText = constructErrorInlineText({
    condition: isEditModeVisible,
    hasError: hasReplyToError,
  });

  const fromNameErrorText = constructErrorInlineText({
    condition: isEditModeVisible,
    hasError: hasFromNameError,
  });

  const CCErrorText = constructErrorInlineText({
    condition: isEditModeVisible,
    hasError: hasCCError,
  });

  const BCCErrorText = constructErrorInlineText({
    condition: isEditModeVisible,
    hasError: hasBCCError,
  });

  const sendersInformationFields: TextFieldProps[] = [
    {
      mediumDataKey: 'fromName',
      labelText: 'label.fromName',
      helpText: 'message.forNameHelpText',
      hasError: hasFromNameError,
      errorText: fromNameErrorText,
      placeholder: 'placeholder.FromEmail',
    },
    {
      mediumDataKey: 'replyTo',
      labelText: 'label.replyToEmail',
      helpText: 'message.replyToEmailHelpText',
      hasError: hasReplyToError,
      errorText: replyToErrorText,
      placeholder: 'placeholder.FromName',
    },
  ];

  const ccField: TextFieldProps = {
    mediumDataKey: 'ccEmails',
    labelText: 'label.CC',
    helpText: 'message.CC',
    placeholder: 'placeholder.CC',
    valueIsArray: true,
    hasError: hasCCError,
    errorText: CCErrorText,
    Suffix: (
      <CloseButton
        onClick={() => {
          setCCFieldState(!showCCField);
          setCurrentMediumField(ccField.mediumDataKey, '');
        }}
      >
        <Close16 isColorInherited={true} />
      </CloseButton>
    ),
  };

  const bccField: TextFieldProps = {
    mediumDataKey: 'bccEmails',
    labelText: 'label.BCC',
    helpText: 'message.CC',
    placeholder: 'placeholder.BCC',
    valueIsArray: true,
    hasError: hasBCCError,
    errorText: BCCErrorText,
    Suffix: (
      <CloseButton
        onClick={() => {
          setBCCFieldState(!showBCCField);
          setCurrentMediumField(bccField.mediumDataKey, '');
        }}
      >
        <Close16 isColorInherited={true} />
      </CloseButton>
    ),
  };

  const sendingInformation: TextFieldProps[] = [
    ...sendersInformationFields,
    ...(showCCField ? [ccField] : []),
    ...(showBCCField ? [bccField] : []),
  ];

  const handleTextFieldChange = (
    mediumDataKey: TextFieldMediumDataKey,
    newVal: string,
    valueIsArray: boolean,
  ) => {
    if (valueIsArray) {
      setCurrentMediumField(mediumDataKey, processTextArray(newVal));
    } else {
      setCurrentMediumField(mediumDataKey, newVal);
    }
  };

  return (
    <>
      <SettingsSectionHeader>{t('header.sendingInformation')}</SettingsSectionHeader>
      <MessageTypeSelect />
      <FromEmailField />
      {sendingInformation.map(
        ({
          mediumDataKey,
          valueIsArray = false,
          placeholder,
          helpText,
          labelText,
          hasError = false,
          errorText,
          Suffix,
        }) => (
          <Text
            key={mediumDataKey}
            mediumDataKey={mediumDataKey}
            mediumDataValue={emailData?.[mediumDataKey] ?? ''}
            isEditModeVisible={isEditModeVisible}
            showSnippet={false}
            helpText={t(helpText)}
            labelText={t(labelText)}
            placeholderText={t(placeholder || labelText)}
            hasError={hasError}
            errorText={errorText}
            changeHandler={(newVal) => handleTextFieldChange(mediumDataKey, newVal, valueIsArray)}
            Suffix={Suffix}
          />
        ),
      )}
      {showCarbonCopyButtons && (
        <ButtonWrapper>
          <AddButton
            text={t('button.CC')}
            testid="cc-add-button"
            display={!showCCField}
            onClick={() => setCCFieldState(true)}
          />
          <AddButton
            text={t('button.BCC')}
            testid="bcc-add-button"
            display={!showBCCField}
            onClick={() => setBCCFieldState(true)}
          />
        </ButtonWrapper>
      )}
    </>
  );
};
